import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import type { AclProviderResult, EntityMetadata } from "./types";

// --- Types ---

export type OwnershipTransferInfo = {
	previousOwnerId: string;
	newOwnerId: string;
	newOwnerName: string;
};

export type EntityConflict = {
	entityUuid: string;
	entityName: string;
	failedUserIds: string[];
	errors: string[];
};

export type LinkedEntityConflictsInfo = {
	conflicts: EntityConflict[];
	safeEntityUuids: Set<string>;
};

export type AclChangeAnalysis = {
	newAcl: RichAccessControl[];
	linkedEntityConflicts: LinkedEntityConflictsInfo | null;
	ownershipTransfer: OwnershipTransferInfo | null;
	entityMetadataList: EntityMetadata[];
	originalAclResults: Array<AclProviderResult>;
};

// --- Helpers ---

function isMixedEntry(entry: RichAccessControl): boolean {
	return "permissionState" in entry && (entry as { permissionState?: string }).permissionState === "mixed";
}

function findOriginalEntry(
	originalAcl: RichAccessControl[],
	userId: string | undefined,
): RichAccessControl | undefined {
	return originalAcl.find((e) => e.userId === userId);
}

function getNewOwnerIdToRevert(
	ownershipTransfer: OwnershipTransferInfo | null,
	conflictUserIds: Set<string>,
): string | null {
	if (!ownershipTransfer) {
		return null;
	}
	if (!conflictUserIds.has(ownershipTransfer.previousOwnerId)) {
		return null;
	}
	return ownershipTransfer.newOwnerId;
}

function shouldRevertToOriginal(
	entry: RichAccessControl,
	conflictUserIds: Set<string>,
	ownerIdToRevert: string | null,
): boolean {
	if (isMixedEntry(entry)) {
		return true;
	}

	if (entry.userId != null && conflictUserIds.has(entry.userId)) {
		return true;
	}

	if (ownerIdToRevert != null && entry.userId === ownerIdToRevert) {
		return true;
	}

	return false;
}

function resolveEntry(
	entry: RichAccessControl,
	originalAcl: RichAccessControl[],
	conflictUserIds: Set<string>,
	ownerIdToRevert: string | null,
): RichAccessControl | null {
	if (shouldRevertToOriginal(entry, conflictUserIds, ownerIdToRevert)) {
		return findOriginalEntry(originalAcl, entry.userId) ?? null;
	}

	return entry;
}

function appendMissingConflictUsers(
	resolvedAcl: RichAccessControl[],
	failedUserIds: string[],
	originalAcl: RichAccessControl[],
): RichAccessControl[] {
	const resolvedUserIds = new Set(resolvedAcl.map((e) => e.userId));
	const missingEntries = failedUserIds
		.filter((userId) => !resolvedUserIds.has(userId))
		.map((userId) => findOriginalEntry(originalAcl, userId))
		.filter((e): e is RichAccessControl => !!e);

	if (missingEntries.length === 0) {
		return resolvedAcl;
	}
	return [...resolvedAcl, ...missingEntries];
}

// --- Main ---

export function buildPerEntityAcls(analysis: AclChangeAnalysis): Record<string, RichAccessControl[]> {
	const { entityMetadataList, originalAclResults, newAcl, linkedEntityConflicts, ownershipTransfer } = analysis;

	return Object.fromEntries(
		entityMetadataList.map((meta, i) => {
			const originalAcl = originalAclResults[i]?.customerEntityAcl ?? [];
			const conflict = linkedEntityConflicts?.conflicts.find((c) => c.entityUuid === meta.uuid);
			const conflictUserIds = new Set(conflict?.failedUserIds ?? []);
			const ownerIdToRevert = getNewOwnerIdToRevert(ownershipTransfer, conflictUserIds);

			const resolvedAcl = newAcl
				.map((entry) => resolveEntry(entry, originalAcl, conflictUserIds, ownerIdToRevert))
				.filter((acl): acl is RichAccessControl => acl !== null);

			const finalAcl = appendMissingConflictUsers(resolvedAcl, conflict?.failedUserIds ?? [], originalAcl);

			return [meta.uuid, finalAcl];
		}),
	);
}
